import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { normalizeApiErrorDetails, updateAdminApp, type AdminRegistryApp } from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { useToast } from '@/components/ui';
import { APP_TYPE_OPTIONS, type AppTypeValue } from '../apps/useCreateAppForm';

const CODE_PATTERN_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type OrgScopeMode = 'selected_orgs' | 'all_orgs';

interface EditableScalarFields {
    name: string;
    description: string;
    icon_url: string;
    app_type: AppTypeValue;
    owner_name: string;
    owner_email: string;
    web_url: string;
    api_base: string;
    audience: string;
    org_scope_mode: OrgScopeMode;
    supports_role_management: boolean;
}

const SCALAR_KEYS = [
    'name',
    'description',
    'icon_url',
    'app_type',
    'owner_name',
    'owner_email',
    'web_url',
    'api_base',
    'audience',
    'org_scope_mode',
    'supports_role_management',
] as const;

function scalarsFromApp(app: AdminRegistryApp): EditableScalarFields {
    return {
        name: app.name,
        description: app.description,
        icon_url: app.icon_url,
        app_type: (APP_TYPE_OPTIONS.some((o) => o.value === app.app_type)
            ? app.app_type
            : 'web') as AppTypeValue,
        owner_name: app.owner_name,
        owner_email: app.owner_email,
        web_url: app.web_url,
        api_base: app.api_base,
        audience: app.audience,
        org_scope_mode: app.org_scope_mode,
        supports_role_management: app.supports_role_management,
    };
}

function isValidUri(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return true;
    if (trimmed === '*') return false;
    return /^https?:\/\//.test(trimmed);
}

function normalizedUriList(values: string[]): string[] {
    return values.map((v) => v.trim()).filter(Boolean);
}

function sameUriList(a: string[], b: string[]): boolean {
    const na = normalizedUriList(a);
    const nb = normalizedUriList(b);
    return na.length === nb.length && na.every((v, i) => v === nb[i]);
}

export interface EditAppDiffEntry {
    key: string;
    labelKey: string;
    before: string;
    after: string;
}

/**
 * EAA-1846 (design frame 51 "App edit", `/console/apps/:code/edit`): draft
 * state for the app-edit form, hydrated from the registry row rather than
 * empty like `useCreateAppForm` — `code` is deliberately absent from this
 * hook's writable surface (locked in the UI, and `AdminAppUpdateSerializer`
 * itself has no `code` field — every RoleMap/OrganizationAppScope/launcher
 * deep-link join keys off it).
 *
 * Every field is tracked against its ORIGINAL (server) value, not just
 * "touched", because the design's own diff panel ("N thay đổi chưa lưu")
 * needs the before/after pair, and the PATCH payload sends only what
 * actually changed rather than the whole form — a field the operator opened
 * and left alone must not appear as a no-op change in the app's audit log.
 */
export function useEditAppForm(app: AdminRegistryApp | undefined, onSaved: () => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [scalars, setScalars] = useState<EditableScalarFields | null>(null);
    const [redirectUris, setRedirectUris] = useState<string[]>(['']);
    const [postLogoutUris, setPostLogoutUris] = useState<string[]>(['']);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [formError, setFormError] = useState<string | null>(null);
    const original = useRef<{
        scalars: EditableScalarFields;
        redirectUris: string[];
        postLogoutUris: string[];
    } | null>(null);
    const hydratedCode = useRef<string | null>(null);

    // Hydrate once per app (identified by code) — a background refetch of the
    // same app (e.g. after an unrelated mutation elsewhere) must NOT clobber
    // an in-progress edit.
    useEffect(() => {
        if (!app || hydratedCode.current === app.code) return;
        hydratedCode.current = app.code;
        const seeded = scalarsFromApp(app);
        const seededRedirect = app.redirect_uris.length > 0 ? app.redirect_uris : [''];
        const seededPostLogout = app.post_logout_redirect_uris.length > 0
            ? app.post_logout_redirect_uris
            : [''];
        setScalars(seeded);
        setRedirectUris(seededRedirect);
        setPostLogoutUris(seededPostLogout);
        original.current = {
            scalars: seeded,
            redirectUris: seededRedirect,
            postLogoutUris: seededPostLogout,
        };
    }, [app]);

    function setScalar<K extends keyof EditableScalarFields>(
        key: K,
        value: EditableScalarFields[K]
    ) {
        setScalars((prev) => (prev ? { ...prev, [key]: value } : prev));
        setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: [] } : prev));
    }

    function markTouched(key: string) {
        setTouched((prev) => ({ ...prev, [key]: true }));
    }

    const trimmedWebUrl = (scalars?.web_url ?? '').trim();
    const trimmedIconUrl = (scalars?.icon_url ?? '').trim();
    const trimmedOwnerEmail = (scalars?.owner_email ?? '').trim();
    const trimmedName = (scalars?.name ?? '').trim();

    // What actually differs from the server's own values — drives the
    // per-field "đã đổi" badge, the diff panel, the PATCH payload (only
    // changed keys are sent), AND which fields' client-side validity actually
    // gates submit (below): an app saved years ago with a `web_url` that
    // fails today's stricter https-only check must not block an edit to an
    // unrelated field just because the operator never touched that one.
    const changedKeys = new Set<string>();
    if (scalars && original.current) {
        for (const key of SCALAR_KEYS) {
            if (scalars[key] !== original.current.scalars[key]) changedKeys.add(key);
        }
        if (!sameUriList(redirectUris, original.current.redirectUris)) {
            changedKeys.add('redirect_uris');
        }
        if (!sameUriList(postLogoutUris, original.current.postLogoutUris)) {
            changedKeys.add('post_logout_redirect_uris');
        }
    }

    const webUrlValid =
        !changedKeys.has('web_url') || trimmedWebUrl === '' || trimmedWebUrl.startsWith('https://');
    const iconUrlValid =
        !changedKeys.has('icon_url') || trimmedIconUrl === '' || /^https?:\/\//.test(trimmedIconUrl);
    const ownerEmailValid =
        !changedKeys.has('owner_email') ||
        trimmedOwnerEmail === '' ||
        CODE_PATTERN_EMAIL.test(trimmedOwnerEmail);
    const nonEmptyRedirects = normalizedUriList(redirectUris);
    const redirectError = !changedKeys.has('redirect_uris')
        ? null
        : nonEmptyRedirects.length === 0
          ? t('console.apps.createForm.uriRequiredError')
          : redirectUris.some((v) => !isValidUri(v))
            ? t('console.apps.createForm.uriInvalidError')
            : null;
    const postLogoutError =
        changedKeys.has('post_logout_redirect_uris') && postLogoutUris.some((v) => !isValidUri(v))
            ? t('console.apps.createForm.uriInvalidError')
            : null;

    const canSubmit =
        Boolean(scalars) &&
        trimmedName.length > 0 &&
        webUrlValid &&
        iconUrlValid &&
        ownerEmailValid &&
        !redirectError &&
        !postLogoutError;

    const diffEntries: EditAppDiffEntry[] = [];
    if (scalars && original.current) {
        const labelKeys: Record<(typeof SCALAR_KEYS)[number], string> = {
            name: 'console.apps.createPage.nameLabel',
            description: 'console.apps.createPage.descriptionLabel',
            icon_url: 'console.apps.createModal.iconUrlLabel',
            app_type: 'console.apps.createModal.appTypeLabel',
            owner_name: 'console.apps.createPage.ownerNameLabel',
            owner_email: 'console.apps.createPage.ownerEmailLabel',
            web_url: 'console.apps.createPage.webUrlLabel',
            api_base: 'console.apps.createPage.apiBaseLabel',
            audience: 'console.apps.createPage.audienceLabel',
            org_scope_mode: 'console.apps.createModal.orgScopeLegend',
            supports_role_management: 'console.apps.createModal.roleManagementLegend',
        };
        for (const key of SCALAR_KEYS) {
            if (!changedKeys.has(key)) continue;
            const before = original.current.scalars[key];
            const after = scalars[key];
            diffEntries.push({
                key,
                labelKey: labelKeys[key],
                before: typeof before === 'boolean' ? (before ? t('common.yes') : t('common.no')) : String(before || '—'),
                after: typeof after === 'boolean' ? (after ? t('common.yes') : t('common.no')) : String(after || '—'),
            });
        }
        if (changedKeys.has('redirect_uris')) {
            diffEntries.push({
                key: 'redirect_uris',
                labelKey: 'console.apps.createModal.redirectUrisLegend',
                before: normalizedUriList(original.current.redirectUris).join(', ') || '—',
                after: normalizedUriList(redirectUris).join(', ') || '—',
            });
        }
        if (changedKeys.has('post_logout_redirect_uris')) {
            diffEntries.push({
                key: 'post_logout_redirect_uris',
                labelKey: 'console.apps.createModal.postLogoutUrisLegend',
                before: normalizedUriList(original.current.postLogoutUris).join(', ') || '—',
                after: normalizedUriList(postLogoutUris).join(', ') || '—',
            });
        }
    }

    const saveMutation = useMutation({
        mutationFn: () => {
            if (!app || !scalars) throw new Error('App not loaded');
            const payload: Record<string, unknown> = {};
            if (changedKeys.has('name')) payload.name = trimmedName;
            if (changedKeys.has('description')) payload.description = scalars.description.trim();
            if (changedKeys.has('icon_url')) payload.icon_url = trimmedIconUrl;
            if (changedKeys.has('app_type')) payload.app_type = scalars.app_type;
            if (changedKeys.has('owner_name')) payload.owner_name = scalars.owner_name.trim();
            if (changedKeys.has('owner_email')) payload.owner_email = trimmedOwnerEmail;
            if (changedKeys.has('web_url')) payload.web_url = trimmedWebUrl;
            if (changedKeys.has('api_base')) payload.api_base = scalars.api_base.trim();
            if (changedKeys.has('audience')) payload.audience = scalars.audience.trim();
            if (changedKeys.has('org_scope_mode')) payload.org_scope_mode = scalars.org_scope_mode;
            if (changedKeys.has('supports_role_management')) {
                payload.supports_role_management = scalars.supports_role_management;
            }
            if (changedKeys.has('redirect_uris')) payload.redirect_uris = nonEmptyRedirects;
            if (changedKeys.has('post_logout_redirect_uris')) {
                payload.post_logout_redirect_uris = normalizedUriList(postLogoutUris);
            }
            return updateAdminApp(config.apiBase, accessToken as string, app.code, payload);
        },
        onSuccess: (updated) => {
            toast.success(t('console.appDetail.edit.saveSuccessToast', { name: updated.name }));
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            onSaved();
        },
        onError: (error) => {
            if (error instanceof OrgAdminApiError) {
                // EAA-1884: `console.ts`'s `request()` used to always resolve
                // `.details` to `null` (a copy/paste bug, fixed alongside this
                // ticket), so this branch never actually ran against real data
                // before. `normalizeApiErrorDetails` flattens the server's
                // `{code, message}` entries into the plain `string[]` this
                // form's `fieldErrors` state expects.
                const details = normalizeApiErrorDetails(error.details);
                if (Object.keys(details).length > 0) {
                    setFieldErrors(details);
                    return;
                }
                setFormError(error.message);
                return;
            }
            setFormError(t('console.appDetail.edit.saveErrorFallback'));
        },
    });

    function onSubmit() {
        if (!canSubmit || saveMutation.isPending || changedKeys.size === 0) return;
        setFormError(null);
        saveMutation.mutate();
    }

    const codeError = null; // code is not editable here.
    const webUrlError = touched.web_url && trimmedWebUrl && !webUrlValid
        ? t('console.apps.createForm.webUrlInvalidError')
        : null;
    const iconUrlError = touched.icon_url && trimmedIconUrl && !iconUrlValid
        ? t('console.apps.createForm.iconUrlInvalidError')
        : null;
    const ownerEmailError = touched.owner_email && trimmedOwnerEmail && !ownerEmailValid
        ? t('console.apps.createForm.emailInvalidError')
        : null;

    return {
        loaded: Boolean(scalars),
        scalars,
        setScalar,
        markTouched,
        redirectUris,
        setRedirectUris,
        postLogoutUris,
        setPostLogoutUris,
        redirectError: touched.redirect_uris ? redirectError : null,
        postLogoutError: touched.post_logout_redirect_uris ? postLogoutError : null,
        codeError,
        webUrlError,
        iconUrlError,
        ownerEmailError,
        fieldErrors,
        formError,
        canSubmit,
        changedKeys,
        diffEntries,
        unsavedCount: changedKeys.size,
        saveMutation,
        onSubmit,
    };
}

export type EditAppFormVM = ReturnType<typeof useEditAppForm>;
